import { Injectable } from "@angular/core";
import { Subject, BehaviorSubject } from "rxjs";
import moment from "moment";

import {
  ActionEvents,
  Actions,
  BrowserMappings,
  PlaybackActionStates,
  PlaybackStates,
} from "../../shared/constants/enums";
import { IAssertion } from "../interfaces/IAssertion";

import { ApplicationsService } from "./applications.service";
import { ToasterService } from "./toaster.service";
import { SessionService } from "./session.service";
import { ReportsService } from "./reports.service";
import { LoggingService } from "./logging.service";
import { PlayBackService } from "./playback.service";
import { SofyLiveService } from "./sofylive.service";
import { OverlayService } from "./overlay.service";
import { UtilsService } from "./utils.services";
import {
  IDEMessageTypes,
  IDEPlayBackMessageTypes,
  DEVICE_ORIENTATION,
} from "src/app/shared/constants/constants";
import { DataDogService } from "./dataDog.service";
import { IActionEvent } from "../interfaces/IActionEvent";
import { IVariableDictionary } from "../interfaces/VariableDictionary";

@Injectable({
  providedIn: "root",
})
export class SessionSocketService {
  SessionSocket: WebSocket;
  SocketIDEMessages: Subject<string> = new Subject<string>();
  SocketMessagesText: Subject<string> = new Subject<string>();
  ScenarioGUID: BehaviorSubject<string> = new BehaviorSubject<string>("");
  trainingScenarioID: BehaviorSubject<string> = new BehaviorSubject<string>("");
  accessibilityTestCompleted: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  canvasDisabled: BehaviorSubject<any> = new BehaviorSubject<boolean>(false);
  dynamicTestCaseVariable: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  APIStepVariable: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  fetchXMLLoader: BehaviorSubject<any> = new BehaviorSubject<boolean>(false);
  IsTemplate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  DragDropPerformed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  firstPlaybackCompleted: BehaviorSubject<any> = new BehaviorSubject<boolean>(
    false
  );
  testFlightVariable: BehaviorSubject<any> = new BehaviorSubject<boolean>(
    false
  );
  applicationInstalled: BehaviorSubject<any> = new BehaviorSubject<boolean>(
    false
  );
  refreshAutomateRuns: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  SocketMessagesImage: Subject<any> = new Subject<any>();
  SessionVerifiedBySocket = false;
  isSocketInitialized = false;
  isPlayBackRun: boolean = false;
  isTemplateMood: boolean = false;
  isLogsPushed: boolean = false;
  isManualTestMood: boolean = false;
  isAutomatedTestMood: boolean = false;
  firstPlayBackMood: boolean = false;
  isInsertedStepMood: boolean = false;
  insertedStepPopup: boolean = false;
  recordingService: any;
  isPromptRecordingComplete: Subject<boolean> = new Subject<boolean>();
  isInsertStepFailure: Subject<boolean> = new Subject<boolean>();
  variableDictionary: BehaviorSubject<IVariableDictionary> =
    new BehaviorSubject<IVariableDictionary>({});
  isConnected: boolean;
  reconnectAttempts = 0;
  maxReconnectAttempts = 3; // Maximum number of reconnection attempts
  reconnectDelay = 5000;
  // For Accessability
  isAccessabilityReportUploaded: Subject<boolean> = new Subject<boolean>();
  allowLandscapeInAccessibility: Subject<boolean> = new Subject<boolean>();
  isAccessabilityReportFailedUploaded: Subject<boolean> =
    new Subject<boolean>();

  isAccessabilityScanningStart: boolean = false;
  isDriverCrashedRestarting: boolean = false;
  private frameReceived: boolean = false;
  private isImageStreamLogged: boolean = false;

  constructor(
    private appService: ApplicationsService,
    private toasterService: ToasterService,
    private sessionService: SessionService,
    private applicationService: ApplicationsService,
    private utilsService: UtilsService,
    private loggingService: LoggingService,
    private reportsService: ReportsService,
    private playBackService: PlayBackService,
    private sofyLiveService: SofyLiveService,
    private overLayService: OverlayService,
    private dataDogService: DataDogService
  ) {}

  // ---------------- Initialize Socket ----------------
  initializeSessionSocket(session: any, sessionExpired: boolean): void {
    if (this.isSocketInitialized) return;
    this.isSocketInitialized = true;
    console.log("Initializing Session Socket Service");

    if (session && sessionExpired === false) {
      this.connectToWebSocket(session);
    } else {
      console.log("Session parameters missing. Cannot connect socket.");
      this.dataDogService.sendMessage(
        `Session parameters missing: ${this.sessionService.SessionGUID}`,
        {
          socketError: true,
          userGUiD: this.applicationService.parentGUID$.getValue(),
          deviceName: this.sessionService.DeviceName,
          sessionGuid: this.sessionService.SessionGUID,
          email: this.sessionService.Email,
        }
      );
    }
  }

  // ---------------- Check Socket ----------------
  isSocketOpen(): boolean {
    this.isConnected =
      this.SessionSocket && this.SessionSocket.readyState === WebSocket.OPEN;
    console.log("Connection Status:", this.isConnected);
    return this.isConnected;
  }

  // ---------------- Connect Socket ----------------
  connectToWebSocket(session: any) {
    console.log("Connecting socket ...");

    this.SessionSocket = new WebSocket("wss://" + session.DNS);
    // If offline or failed to connect, trigger reconnect
    if (navigator.onLine && !this.isSocketOpen()) {
      this.registerEvents(session);
    } else {
      console.error("Offline or failed to connect. Attempting reconnect...");
      this.handleReconnect(session);
    }
  }

  // ---------------- Reconnect Logic ----------------
  handleReconnect(session: any) {
    // Stop if max attempts exceeded
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.overLayService.SetOverlayText(
        "Connection lost, please reconnect and try again."
      );
      this.overLayService.HideLoading();
      this.overLayService.ShowOverlay();
      console.error("❌ Cannot reconnect. Maximum attempts reached.");
      return;
    }

    // Show reconnecting overlay
    this.overLayService.SetOverlayText(
      "Internet Disconnected",
      "Hang tight - We’re attempting to re-establish the connection to resume your session automatically."
    );
    this.overLayService.ShowOverlay();

    // Increase attempt count
    this.reconnectAttempts++;
    console.log(
      `🔁 Reconnect Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`
    );

    // Progressive delay: 5s, 10s, 15s...
    const delay = this.reconnectDelay * this.reconnectAttempts;
    console.warn(`Retrying socket in ${delay / 1000}s...`);

    setTimeout(() => {
      if (!this.isSocketOpen() && !this.SessionVerifiedBySocket) {
        console.log("Trying to reconnect socket...");
        this.connectToWebSocket(session);
        // Try again after next delay if still disconnected
        this.handleReconnect(session);
      } else {
        console.log("✅ Socket reconnected successfully!");
        this.reconnectAttempts = 0; // Reset attempts
        this.overLayService.HideLoading();
        this.overLayService.HideOverlay();
      }
    }, delay);
  }

  // ---------------- Reconnect Wrapper ----------------
  reconnectToWebSocket(session: any) {
    console.log("Reconnecting socket ...");
    this.handleReconnect(session);
  }

  // ---------------- Disconnect ----------------
  disconnectToWebSocket() {
    if (this.SessionSocket) {
      console.log("Disconnecting socket ...");
      this.dataDogService.sendMessage(
        `Disconnecting socket ${this.sessionService.SessionGUID}`,
        {
          userGUiD: this.applicationService.parentGUID$.getValue(),
          sessionGuid: this.sessionService.SessionGUID,
        }
      );
      this.reportsService
        .addLogs(
          "SOCKET",
          "SESSION",
          "Disconnecting socket",
          new Date().toISOString(),
          { sessionGuid: this.sessionService.SessionGUID }
        )
        .subscribe(() => {});
      this.SessionSocket.close();
    }
  }

  // ---------------- Register Events ----------------
  registerEvents(session: any) {
    if (!this.SessionSocket) return;

    // ---------- onopen ----------
    this.SessionSocket.onopen = () => {
      console.log(
        "✅ WebSocket connected successfully and sent a request for image streaming"
      );
      this.reconnectAttempts = 0; // reset on success
      this.SessionVerifiedBySocket = true;
      this.overLayService.HideOverlay();

      const info = {
        userGUID: this.applicationService.parentGUID$.getValue(),
        deviceName: this.sessionService.DeviceName,
        sessionGuid: this.sessionService.SessionGUID,
        email: this.sessionService.Email,
        sessionType: this.sessionService.PlatformID === 1 ? "Android" : "iOS",
        isAcquired: true,
        finalAcquire: true,
      };
      this.dataDogService.sendMessage(`WebSocket connection established`, info);

      this.imageStreamingRequest?.();
      this.handleOpenSocketCase(session.SessionGUID);
    };

    // ---------- onmessage ----------
    this.SessionSocket.onmessage = (message) => {
      try {
        this.handleSocketMessage(message.data);
      } catch (err) {
        console.error("Error processing WebSocket message:", err);
        this.dataDogService.sendMessage(`Error processing WebSocket message`, {
          sessionGuid: this.sessionService.SessionGUID,
          error: err.message,
          messageData: message.data,
        });
      }
    };

    // ---------- onerror ----------
    this.SessionSocket.onerror = (error) => {
      console.error("WebSocket error:", error);
      this.dataDogService.sendMessage(`WebSocket error detected`, {
        sessionGuid: this.sessionService.SessionGUID,
        error,
      });
      this.handleReconnect(session);
    };

    // ---------- onclose ----------
    this.SessionSocket.onclose = (closeEvent) => {
      const { code, reason, wasClean } = closeEvent;
      console.log(
        `WebSocket closed. Code: ${code}, Reason: ${reason}, Clean: ${wasClean}`
      );
      this.dataDogService.sendMessage(`WebSocket closed`, {
        code,
        reason,
        wasClean,
      });

      if (!wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
        console.warn("Unexpected close. Attempting reconnect...");
        this.handleReconnect(session);
      }
      this.handleEndSession();
    };
  }
  private handleSocketMessage(data: any): void {
    if (data.size) {
      if (!this.isImageStreamLogged) {
        console.log("Image streaming received successfully");
        this.isImageStreamLogged = true;

        this.dataDogService.sendMessage(
          `Image streaming started successfully for session ${this.sessionService.SessionGUID}`,
          {
            status: "success",
            message: "Image streaming received successfully",
            userGUiD: this.applicationService.parentGUID$.getValue(),
            deviceName: this.sessionService.DeviceName,
            sessionGuid: this.sessionService.SessionGUID,
            email: this.sessionService.Email,
          }
        );
      }
      this.frameReceived = true;
      this.handleImageData(data);
    } else {
      this.handleNonImageData(data);
    }

    setTimeout(() => {
      if (!this.frameReceived) {
        this.sessionService.ReleaseSessionWithEffect().subscribe((session) => {
          if (!this.isLogsPushed) {
            this.isLogsPushed = true;
            this.reportsService
              .addLogs(
                "STREAM",
                "SESSION",
                "Device initialization failed: no frames were received within 60 seconds. Session has been terminated",
                moment().format("YYYY-MM-DD HH:mm:ss"),
                { sessionGuid: this.sessionService.SessionGUID }
              )
              .subscribe(() => {});
          }
          this.dataDogService.sendMessage(
            `Device initialization failed - session ended ${this.sessionService.SessionGUID}`,
            {
              errorMsg:
                "Session ended — no frames received from the device" +
                session.message,
              userGUiD: this.applicationService.parentGUID$.getValue(),
              deviceName: this.sessionService.DeviceName,
              sessionGuid: this.sessionService.SessionGUID,
              email: this.sessionService.Email,
            }
          );
          this.overLayService.SetOverlayText(
            "Device failed to initialize, please try a different device"
          );
          this.overLayService.HideLoading();
          this.overLayService.ShowOverlay();

          console.error(
            "Session ended — no frames received from the device" +
              session.message
          );
        });
      }
    }, 60000);
  }

  private handleImageData(data: any): void {
    this.SocketMessagesImage.next(data);
  }

  private handleNonImageData(data: string): void {
    if (data.startsWith("fetch_data_emit")) {
      console.log("Received: fetch_data_emit");
    } else {
      console.log(
        `Received: ${moment().format("YYYY-MM-DD HH:mm:ss")} ${data}`
      );

      /* Use the confirmation of location update to set those lat lng values 
       that's in turn used to open updated location map */
      this.utilsService.updateLatLng(data);
    }
    this.EnumerateMessageEmitted(data);
  }

  imageStreamingRequest() {
    this.sendSocketEvent("*");
  }

  showErrorWithoutTryAgain() {
    const overlayVal = this.overLayService.overlay.getValue();
    if (overlayVal) {
      this.overLayService.showError();
      this.overLayService.disableTryAgain();
    }
  }

  showError() {
    this.overLayService.showError();
  }

  receivedRecordingService(recordingService: any) {
    this.recordingService = recordingService;
  }

  //  Receiving Event from the IDE side
  EnumerateMessageEmitted(ideMessage: string): void {
    switch (true) {
      case ideMessage.startsWith(IDEMessageTypes.FETCH_DATA_RESP):
        this.handleFetchDataResponse(ideMessage);
        break;
      case ideMessage.includes("scenario_steps"):
        this.handleFetchDataResponse(ideMessage);
        break;
      case ideMessage.includes(IDEMessageTypes.SCENARIO_SAVE_FAILED):
        this.handleExceptioninSavingScenario();
        break;
      case ideMessage.includes(IDEMessageTypes.SCENARIO_SAVE_SUCCESSFULL):
        this.handleSuccessfullSavingScenario();
        break;
      case ideMessage.includes(IDEMessageTypes.DELETE_STEP_SUCCESSFUL):
        this.handleSuccessfulDeletion(ideMessage);
        break;
      case ideMessage.includes(IDEMessageTypes.DELETE_STEP_FAILED):
        this.handleFailedStepDeletion(ideMessage);
        break;
      case ideMessage.includes(IDEMessageTypes.EDIT_STEP_FAILED):
        this.handleFailedStepEdit(ideMessage);
        break;
      case ideMessage.includes(IDEMessageTypes.ACCESSABILITY_TEST_START):
        this.handleAccessibilityStarted();
        break;
      case ideMessage.includes(IDEMessageTypes.ACCESSABILITY_TEST_COMPLETE):
        this.handleAccessibilityCompleted();
        break;
      case ideMessage.includes(IDEMessageTypes.ACCESSABILITY_REPORT_UPLOADED):
        this.handleAccessibilityReportUploaded();
        break;
      case ideMessage.includes(IDEMessageTypes.ACCESSABILITY_REPORT_GUID):
        this.handleAccessibilityReportGuid(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.NETWORK_LOG_UPLOADED):
        this.handleNetworkLogsDownload(ideMessage);
        break;
      case ideMessage.includes(
        IDEMessageTypes.ACCESSABILITY_REPORT_FAILED_TO_UPLOADED
      ):
        this.handleAccessibilityReportFailedUploaded();
        break;
      case ideMessage.startsWith(IDEMessageTypes.APP_FAIL):
        this.handleApplicationFailed(true);
        break;
      case ideMessage.startsWith(IDEMessageTypes.STATUS):
        this.handleRealTimeIdeLog(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.DOWNLOAD_DOWNLOAD):
        this.handleDownloadScenario(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.GET_THIRD_PARTY_APP):
        this.handleThirdPartyApps(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.DOWNLOAD):
        this.handleApplicationDownload(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.PLAYBACK):
        this.handlePlaybackStatus(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.VALIDATION):
        this.handlePlaybackStatus(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.UPLOAD_APP):
        this.handleAppUpload(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.DEVICE_LOG):
        this.handleDeviceLog(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.BUG_REPORT):
        this.handleBugReport(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.APPLICATION_STATE):
        this.handleApplicationState(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.END_SESSION):
        this.handleEndSession();
        break;
      case ideMessage.startsWith(IDEMessageTypes.STATUS_CHECK):
        this.handleSocketsStatus(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.SCENARIO_GUID):
        console.log("socket:343");
        this.handleUploadScenario(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.TRAINING_SCENARIO_ID):
        this.handleUploadScenarioTrainingID(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.TEMPLATE_GUID):
        this.handleUploadScenario(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.FETCH_API_STEP_VARIABLES):
        this.handleFetchAPIStepVariables(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.FETCH_TEST_CASE_VARIABLES):
        this.handleFetchTestCaseVariables(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.PROMPT_RECORDING_COMPLETE):
        this.handlePromptRecordingComplete(ideMessage);
        break;
      case ideMessage.startsWith(IDEMessageTypes.SWIPE_UNTIL_START) ||
        ideMessage.startsWith(IDEMessageTypes.SWIPE_UNTIL_CANCELLED) ||
        ideMessage.startsWith(IDEMessageTypes.SWIPE_UNTIL_SAVED):
        this.handleSwipeUntilUIChanges(ideMessage);
        break;
      case ideMessage.includes(IDEMessageTypes.SWIPE_UNTIL_COMPONENT_DETAILS):
        this.handleSwipeUntilComponentData(ideMessage);
        break;
      case ideMessage.includes(IDEMessageTypes.SWIPE_UNTIL_SWIPE_COUNT):
        this.handleSwipeUntilComponentData(ideMessage);
        break;
      case ideMessage.includes(IDEMessageTypes.DRAG_DROP_PERFORMED):
        this.handleDragDropMessage();
        break;
      default:
        this.handleDefaultSocketsEvent(ideMessage);
        break;
    }
    this.handleInsertedStepPopup(ideMessage);
  }

  handleAppiumCrash(ideMessage) {
    let message = ideMessage.split(
      "           -------------------           "
    )[0];
    this.toasterService.ShowToastMessage(message, 0, 5000);
    setTimeout(() => {
      this.handleEndSession();
    }, 10000);
  }

  // Prompt Recording Complete

  handlePromptRecordingComplete(ideMessage) {
    if (ideMessage.includes("promptRecord:complete")) {
      this.isPromptRecordingComplete.next(true);
    }
  }

  handleFetchDataResponse(ideMessage: string) {
    this.playBackService.PlaybackComponentStringEmittedSocket(ideMessage);
  }

  handleExceptioninSavingScenario() {
    this.toasterService.ShowToastMessage("Scenario Save Failed", 0, 5000);
    if (this.playBackService.newDraftTestCaseValidationStarted) {
      this.playBackService.newDraftTestCaseValidationStarted.next(false);
    }
  }

  handleSuccessfullSavingScenario() {
    this.toasterService.ShowToastMessage(
      "Scenario Saved Successfully",
      1,
      5000
    );
  }

  // POST HOOK TEMPLATE INSERTION
  handlePostHookTemplateInsertion(ideMessage: string) {
    this.toasterService.ShowToastMessage(
      "Post Hook Template Added Successfully",
      1,
      5000
    );
  }

  handleDragDropMessage() {
    this.DragDropPerformed.next(true);
    this.toasterService.ShowToastMessage("Drag and Drop performed successfully", 1, 3000);
  }

  handleApplicationFailed(value: boolean) {
    this.applicationInstalled.next(value);
    this.toasterService.ShowToastMessage("Application Load Fail", 0, 3000);
  }

  handleRealTimeIdeLog(ideMessage: string) {
    const realtimeMessageObject = JSON.parse(ideMessage.substr(7));
    this.loggingService.sendLog(realtimeMessageObject.msg + "\n");
    if (ideMessage.includes(IDEMessageTypes.APPIUM_CRASH)) {
      this.handleAppiumCrash(realtimeMessageObject.msg);
    }
    if (this.recordingService.isRecording && (ideMessage.includes(IDEMessageTypes.PERFORMING_TOUCH_ACTION) || ideMessage.includes(IDEMessageTypes.XML_FETCHED) || ideMessage.includes(IDEMessageTypes.SWIPE_PERFORMED))) {
      this.SocketMessagesText.next(ideMessage);
    }
    if(ideMessage.includes(IDEMessageTypes.DRIVER_CRASHED_RESTARTING) || ideMessage.includes(IDEMessageTypes.RECOVERING_THE_DRIVER)) {
      this.handleDriverCrashedRestarting(realtimeMessageObject.msg);
    }
    if(ideMessage.includes(IDEMessageTypes.DRIVER_ONLINE) || ideMessage.includes(IDEMessageTypes.DRIVER_RECOVERED_SUCCESSFULLY)) {
      this.handleDriverOnline(realtimeMessageObject.msg);
    }
    if(ideMessage.includes(IDEMessageTypes.SCENARIO_NOT_UPLOAD)) {
      this.isInsertStepFailure.next(true);
      this.toasterService.ShowToastMessage("Insert steps failed", 0, 5000);
    }
  }

  handleDownloadScenario(ideMessage: string) {
    if (ideMessage.includes("Complete")) {
      this.playBackService.SetPlaying();
    } else {
      this.playBackService.SetCurrentPlayableAutomatedTestCaseDownloading();
      const percent = parseFloat(ideMessage.substr(20, 2));
      this.playBackService.SetCurrentPlayableAutomatedTestCaseDownloadPercent(
        percent
      );
    }
  }

  handleThirdPartyApps(ideMessage: string) {
    JSON.parse(ideMessage.substr(17));
  }

  handlePlaybackStatus(ideMessage: string) {
    const playableCases =
      this.playBackService.SelectedTestCasesToPlay$.getValue();

    switch (true) {
      case ideMessage.includes(IDEPlayBackMessageTypes.PLAYBACK_STARTED):
        this.toasterService.ShowToastMessage("Playback Started", 1, 5000);
        this.playBackService.playbackValidationStatus.next(null);
        this.playBackService.SetPlaying();
        this.RequestPlaybackComponents();
        break;

      case ideMessage.includes(IDEPlayBackMessageTypes.PLAYBACK_COMPLETED):
        this.toasterService.ShowToastMessage("Playback Completed", 1, 5000);
        // if (!this.recordingService.onboardingComplete)
        //   this.firstPlaybackCompleted.next(true);
        this.recordingService.onboardingComplete.subscribe((elem) => {
          if (elem) {
            this.firstPlaybackCompleted.next(true);
          }
        });
        this.playBackService.SetCurrentPlayableAutomatedTestCaseCompleted();
        break;

      case ideMessage.includes(IDEPlayBackMessageTypes.VALIDATION_COMPLETED):
        this.toasterService.ShowToastMessage("Validation Completed", 1, 5000);
        this.recordingService.onboardingComplete.subscribe((elem) => {
          if (elem) {
            this.firstPlaybackCompleted.next(true);
          }
        });
        this.playBackService.playbackValidationStatus.next(
          PlaybackActionStates.PASS
        );
        this.playBackService.SetValidationAutomatedTestCaseCompleted();
        if (!playableCases) {
          this.playBackService.PlaybackStatus$.next(PlaybackStates.IDLE);
        }
        break;

      case ideMessage.includes(
        IDEPlayBackMessageTypes.PLAYBACK_UPLOADING_RESULTS
      ):
        this.playBackService.SetCurrentPlayableAutomatedTestCaseUploading();
        break;

      case ideMessage.includes(
        IDEPlayBackMessageTypes.PLAYBACK_UPLOADED_RESULTS
      ):
        if (!playableCases) {
          this.playBackService.PlaybackStatus$.next(PlaybackStates.IDLE);
        }
        break;

      case ideMessage.includes("true") || ideMessage.includes("false"):
        this.toasterService.ShowToastMessage(ideMessage, 1, 5000);
        break;

      case ideMessage.includes(IDEPlayBackMessageTypes.PLAYBACK_STOPPED):
        this.toasterService.ShowToastMessage("Playback Stopped", 1, 5000);
        this.playBackService.stoppedPlayBack();
        break;

      case ideMessage.includes(IDEPlayBackMessageTypes.VALIDATION_STOPPED):
        this.toasterService.ShowToastMessage("Playback Stopped", 1, 5000);
        this.playBackService.playbackValidationStatus.next(null);
        this.playBackService.isAutomatedRunValidationStarted = false;
        this.playBackService.newDraftTestCaseValidationStarted.next(false);
        this.playBackService.stoppedPlayBack();
        break;

      case ideMessage.includes(IDEPlayBackMessageTypes.PLAYBACK_FAILED):
        this.toasterService.ShowToastMessage("Playback Failed", 0, 2000);
        if (!this.recordingService.onboardingComplete)
          this.firstPlaybackCompleted.next(true);
        this.playBackService.SetCurrentPlayableAutomatedTestCaseCompleted();
        if (!playableCases) {
          this.playBackService.PlaybackStatus$.next(PlaybackStates.IDLE);
        }
        break;

      case ideMessage.includes(IDEPlayBackMessageTypes.VALIDATION_FAILED):
        this.toasterService.ShowToastMessage("Validation Failed", 0, 2000);
        if (!this.recordingService.onboardingComplete)
          this.firstPlaybackCompleted.next(true);
        this.playBackService.playbackValidationStatus.next(
          PlaybackActionStates.FAIL
        );
        this.playBackService.SetValidationAutomatedTestCaseCompleted();
        break;

      default:
        const indStatusString = ideMessage.split(":")[1].split(" ");
        const ind = parseInt(indStatusString[0], 10);
        const stat = indStatusString[1];
        this.playBackService.CurrentPlaybackIndexStatus$.next({
          index: ind,
          status: stat,
        });
        break;
    }
  }

  handleApplicationDownload(ideMessage: string) {
    const x1 = ideMessage.substr(ideMessage.indexOf(":") + 1);
    if (x1 === "Complete") {
      this.appService.SetApplicationDownloadPercentage(100);
    } else {
      const perc = parseFloat(x1.split(".")[0]);
      this.appService.SetApplicationDownloadPercentage(perc);
    }
  }

  handleAppUpload(ideMessage: string) {
    const _app = this.appService.SelectedApplication.getValue();

    if (ideMessage.includes("UploadApp:true")) {
      this.toasterService.ShowToastMessage("Application Installed", 1, 5000);
      // this.playBackService.getAutomatedTestCaseList();
      if (_app.buildsList) {
        this.appService.initiateApplicationInstallOrToggleSelected(
          _app.buildsList[_app.selectedBuildIndex].value
        );
      } else {
        this.appService.initiateApplicationInstallOrToggleSelected(
          _app.UserApplicationBuildList[_app.selectedBuildIndex].AppPackageGUID
        );
      }
      if (this.appService.appInstallStartTime) {
        const duration = Math.floor(
          (Date.now() - this.appService.appInstallStartTime) / 1000
        );
        
        this.sessionService.appDownloadDuration(
          _app.ApplicationGUID,
          _app.buildsList
            ? _app.buildsList[_app.selectedBuildIndex].value
            : _app.UserApplicationBuildList[_app.selectedBuildIndex]
                .AppPackageGUID,
          duration
        );
        this.appService.appInstallStartTime = 0;
      }
    } else if (
      ideMessage.includes("UploadApp:false INSTALL_FAILED_OLDER_SDK")
    ) {
      this.toasterService.ShowToastMessage(
        "Application failed to install due to incompatible OS version",
        0,
        5000
      );
      this.appService.applicationInstallFailed(
        _app.UserApplicationBuildList[0].AppPackageGUID,
        true
      );
    } else {
      const Message = ideMessage.substring(ideMessage.indexOf(":") + 6);
      this.dataDogService.sendMessage(
        `Application installation failed ${this.sessionService.SessionGUID}`,
        {
          error: Message,
        }
      );
      this.toasterService.ShowToastMessage(
        "Application Install Failed",
        0,
        2000,
        Message
      );
      this.appService.applicationInstallFailed(
        _app.UserApplicationBuildList[0].AppPackageGUID,
        true
      );
    }
  }

  handleDeviceLog(ideMessage: string) {
    this.sofyLiveService.issueCreateModal_DeviceLogUrl.next(
      ideMessage.substr(10)
    );
  }

  handleBugReport(ideMessage: string) {
    this.sofyLiveService.issueCreateModal_BugReportUrl.next(
      ideMessage.substr(10)
    );
  }

  handleApplicationState(ideMessage: string) {
    if (ideMessage.includes("Crashed")) {
      this.dataDogService.sendMessage(
        `Application Crashed ${this.sessionService.SessionGUID}`,
        {
          error: ideMessage,
        }
      );
      this.toasterService.ShowToastMessage(
        "Application Crashed",
        0,
        5000,
        "Application Crashed - Trying to Relaunch."
      );
    } else if (ideMessage.includes("Relaunched")) {
      this.dataDogService.sendMessage(
        `Application Relaunched ${this.sessionService.SessionGUID}`,
        {
          error: ideMessage,
        }
      );
      this.toasterService.ShowToastMessage("Application Relaunched.", 1, 5000);
    }
  }

  handleEndSession() {
    this.sessionService.ReleaseSessionWithEffect().subscribe((session) => {
      this.overLayService.SetOverlayText("Session Ended");
      this.overLayService.HideLoading();
      this.overLayService.ShowOverlay();
    });
  }

  handleSocketsStatus(ideMessage: string) {
    if (ideMessage.includes("true")) {
      this.SessionVerifiedBySocket = true;
      this.SocketMessagesText.next(ideMessage);
    } else {
      this.disconnectToWebSocket();
    }
  }

  handleUploadScenario(ideMessage: string) {
    // this.recordingService.ResetAutomatedRecording();
    let guid = ideMessage.substr(15);
    this.refreshAutomateRuns.next(true);
    this.SocketMessagesText.next(ideMessage);
  }
  handleUploadScenarioTrainingID(ideMessage: string) {
    let trainingID = Number(ideMessage.substr(20));
    if (
      this.playBackService.newDraftTestCaseValidationStarted.getValue() === true
    ) {
      this.playBackService.newDraftTestCaseTrainingID.next(trainingID);
    }
    this.SocketMessagesText.next(ideMessage);
  }

  handleFetchAPIStepVariables(ideMessage: string) {
    const variables = JSON.parse(ideMessage.split("FetchAPIStepVariables:")[1]);

    const updatedVariables = [];
    for (const key in variables) {
      if (variables.hasOwnProperty(key)) {
        const variable = variables[key];
        updatedVariables.push({
          id: key,
          variableValue: variable.value,
          variableName: variable.name,
          regex: variable?.regex ?? "",
          type: variable.type,
          creation_step_index: variable?.creation_step_index,
          variable_type: variable?.variable_type || "String",
          apiVariablePath: variable?.apiVariablePath,
        });
      }
    }
    this.APIStepVariable.next(updatedVariables);
  }

  handleFetchTestCaseVariables(ideMessage: string) {
    const variables = JSON.parse(
      ideMessage.split("FetchTestCaseVariables:")[1]
    );
    const updatedVariables = [];
    for (const key in variables) {
      if (variables.hasOwnProperty(key)) {
        const variable = variables[key];
        updatedVariables.push({
          id: key,
          variableValue: variable.value,
          variableName: variable.name,
          regex: variable.regex,
          type: variable.type,
          variable_type: variable?.variable_type || "String",
          creation_step_index: variable?.creation_step_index,
          expression: variable?.expression,
          no_of_decimals: variable?.no_of_decimals,
        });
      }
    }
    this.dynamicTestCaseVariable.next(updatedVariables);
  }

  handleDefaultSocketsEvent(ideMessage: string) {
    this.SocketMessagesText.next(ideMessage);
  }
  handleInsertedStepPopup(ideMessage: string): void {
    if (this.isInsertedStepMood) {
      if (ideMessage.includes("InsertedSteps:true")) {
        this.insertedStepPopup = true;
        this.recordingService.ResetAutomatedRecording();
      }
    } else if (ideMessage.includes("Inserting Steps Failed")) {
      this.insertedStepPopup = true;

      this.recordingService.ResetAutomatedRecording();
    }
  }

  // Sending Event Lab side to IDE
  sendSocketEvent(message: any) {
    // Check if In accessbility report
    if (this.isAccessabilityScanningStart) {
      if (
        message ===
          `${ActionEvents.PerformAction} ${Actions.ROTATE_DEVICE} ${DEVICE_ORIENTATION.PORTRAIT}` ||
        message ===
          `${ActionEvents.PerformAction} ${Actions.ROTATE_DEVICE} ${DEVICE_ORIENTATION.LANDSCAPE}`
      ) {
        this.allowLandscapeInAccessibility.next(true);
      } else {
        this.allowLandscapeInAccessibility.next(false);
      }
    }

    if (this.SessionSocket?.readyState === WebSocket.OPEN) {
      this.SessionSocket.send(message);
      if (message !== "*") {
        this.logSentMessage(message);
      }
    } else {
      console.warn("WebSocket is not open. Message not sent.");
      this.dataDogService.sendMessage(
        `WebSocket is not open. Message not sent ${this.sessionService.SessionGUID}`,
        {
          socketError: true,
          userGUiD: this.applicationService.parentGUID$.getValue(),
        }
      );
    }
  }

  private logSentMessage(message: string): void {
    const formattedTimestamp = moment().format("YYYY-MM-DD HH:mm:ss");
    console.log(`Sent: ${formattedTimestamp} ${message}`);
  }

  PlaybackScenarioByScenarioGUID(ScenarioGUID: string, version?: string): void {
    if (version) {
      const encodedName = encodeURIComponent(version);
      // Sending versin name with scenario guid
      this.sendSocketEvent("3" + " " + ScenarioGUID + " " + encodedName);
    } else {
      this.sendSocketEvent("3" + " " + ScenarioGUID);
    }
  }

  RequestPlaybackStepReport(index) {
    this.sendSocketEvent("27" + " " + index);
  }

  SendKeyInputEvent(keyAsciiCode: number): void {
    this.sendSocketEvent(
      ActionEvents.KeyIputEvent + " " + keyAsciiCode.toString()
    );
  }

  SendClickEmit(clickData: ArrayBuffer): void {
    this.sendSocketEvent(clickData);
  }

  SendManualEnd(testCaseResultId: number, testCaseResultStatus: number): void {
    this.sendSocketEvent(
      "26" +
        " " +
        testCaseResultId.toString() +
        " " +
        testCaseResultStatus.toString()
    );
  }

  SendFetchDeviceLogURL(): void {
    this.sendSocketEvent("23");
  }

  SendFetchBugReportURL(): void {
    this.sendSocketEvent("24");
  }
  sendTestFlightEvent(): void {
    this.sendSocketEvent(`420`);
  }

  //Delete Step while Recording index send
  sendIndexOfStepToDelete(index: number) {
    this.sendSocketEvent("107" + " " + index);
  }

  sendIndexOfStepToEdit(index: number) {
    console.log("108" + " " + index);
    this.sendSocketEvent("108" + " " + index);
  }

  sendEditStepData(index: number, data) {
    console.log("108" + " " + index);
    this.sendSocketEvent("110" + " " + index + " " + JSON.stringify(data));
  }
  //Step deletion successful message
  handleSuccessfulDeletion(ideMessage: string) {
    this.toasterService.ShowToastMessage("Step Deleted Successfully", 1, 5000);
    this.SocketMessagesText.next(ideMessage);
  }

  //Step deletion failed while recording due to invalid index
  handleFailedStepDeletion(ideMessage: string) {
    this.toasterService.ShowToastMessage("Step Deletion Failed", 0, 5000);
    this.SocketMessagesText.next(ideMessage);
  }

  handleFailedStepEdit(ideMessage: string) {
    this.toasterService.ShowToastMessage("Edit Step Failed", 0, 5000);
    this.SocketMessagesText.next(ideMessage);
  }

  SendEndAutomatedRecordingSaveToCloud(
    automatedTestCaseName: string = "SOFY_SCENARIO",
    priority: string = "Low",
    description: string = "test case",
    templateGuid: string = "",
    templateId: string = "",
    tags: string = "No tags",
    insertedTestCaseGuid?: string,
    insertedScenario: string = "testcase",
    status: string = "",
    versionName: string = "",
    insertStepsAtEnd: boolean = false,
    stepNumber: number = 0
  ): void {
    const data = {
      name: automatedTestCaseName,
      priority,
      description,
      postHook_templateGuid: templateGuid,
      templateID: templateId,
      tags,
      status,
    };

    const hasInsertionScenario =
      typeof insertedTestCaseGuid === "string" && insertedTestCaseGuid.trim() !== "";

    if (hasInsertionScenario) {
      const insertionData = {
        scenario_guid: insertedTestCaseGuid,
        scenario_type: insertedScenario,
        version_name: encodeURI(versionName),
        insert_at_end: insertStepsAtEnd,
        insert_after: stepNumber,
      };

      this.sendSocketEvent(`9 ${JSON.stringify(insertionData)}`);
      this.recordingService.ResetAutomatedRecording();
    } else {
      this.sendSocketEvent(`10 ${JSON.stringify(data)}`);
    }
  }

  /**
   * Inserts recorded steps into multiple test cases/templates in a single socket call,
   * instead of one call per item. `insertStepsAtEnd`/`stepNumber` apply to every item.
   */
  SendEndAutomatedRecordingInsertMultipleToCloud(
    items: Array<{ scenarioGuid: string; scenarioType: string; versionName?: string }>,
    insertStepsAtEnd: boolean,
    stepNumber: number
  ): void {
    if (!items.length) {
      return;
    }

    const insertionData = items.map((item) => ({
      scenario_guid: item.scenarioGuid,
      scenario_type: item.scenarioType,
      version_name: encodeURI(item.versionName || ''),
      insert_at_end: insertStepsAtEnd,
      insert_after: stepNumber,
    }));

    this.sendSocketEvent(`9 ${JSON.stringify(insertionData)}`);
    this.recordingService.ResetAutomatedRecording();
  }

  SendEndAutomatedRecordingDontSaveToCloud(): void {
    this.sendSocketEvent("25");
  }

  SendForceStopPlaybacks(): void {
    if (this.SessionSocket) {
      this.sendSocketEvent("21");
    }
  }

  SendRerunFromStep(stepNumber: number): void {
    this.sendSocketEvent("28" + " " + stepNumber);
  }

  SendStartManualTesting(): void {
    this.sendSocketEvent("22");
  }

  SendStartManualTestingWithId(
    TestCaseResultId: number,
    notForDemo: boolean = true
  ): void {
    const socketCall = `22 ${TestCaseResultId.toString()} ${
      !notForDemo ? "false" : ""
    }`;
    this.sendSocketEvent(socketCall);
  }

  SendIssueCreation(liveIssueId: number): void {
    this.sendSocketEvent(
      ActionEvents.PerformAction + " " + "32" + " " + liveIssueId.toString()
    );
  }

  SendApplicationInstall(ApplicationPackageGUID: string): void {
    if (this.testFlightVariable.value === false) {
      this.sendSocketEvent(
        ActionEvents.UploadApp + " " + ApplicationPackageGUID
      );
    } else {
      this.sendTestFlightEvent();
    }
  }
  SendOpenBrowser(SessionGUID: string, browserName: string): void {
    this.sendSocketEvent(`20 ${SessionGUID}`);
    this.sendSocketEvent(`37 ${BrowserMappings[browserName]}`);
  }

  SendWebsiteLinkForBrowser(url: string): void {
    this.sendSocketEvent(`38 ${url}`);
  }

  QuerySessionStatus(): void {
    this.sendSocketEvent("status");
  }

  handleOpenSocketCase(sessionGUID: string): void {
    this.sendSocketEvent("session:" + sessionGUID);
    this.sendSocketEvent("status");
  }

  RequestAssertionComponents(): void {
    this.sendSocketEvent("15");
  }

  SendAppCleanUp(cleanUpValue = false): void {
    this.sendSocketEvent(`42 ${cleanUpValue}`);
  }

  SendAPIStep(data: any): void {
    this.sendSocketEvent(`43 ${data}`);
  }

  SendChangeOrientation(Orientation: string = "Portrait"): void {
    this.sendSocketEvent(
      ActionEvents.PerformAction +
        " " +
        Actions.ROTATE_DEVICE +
        " " +
        Orientation
    );
  }

  SendSetText(setTextValue: string): void {
    this.sendSocketEvent(
      ActionEvents.PerformAction + " " + Actions.SET_TEXT + " " + setTextValue
    );
  }

  SendSetTestVariable(testDataVariableId: string): void {
    this.sendSocketEvent(
      ActionEvents.PerformAction +
        " " +
        Actions.SET_TEST_DATA_VARIABLE +
        " " +
        testDataVariableId
    );
  }

  SendSetDyanmicTestCaseVariable(testDataVariableId: string): void {
    this.sendSocketEvent(
      ActionEvents.PerformAction +
        " " +
        Actions.SET_DYNAMIC_TEST_CASE_VARIABLE +
        " " +
        testDataVariableId
    );
  }

  SendAPIStepVariable(testDataVariableId: string): void {
    this.sendSocketEvent(
      ActionEvents.PerformAction +
        " " +
        Actions.SET_API_STEP_VARIABLE +
        " " +
        testDataVariableId
    );
  }

  SendAssertItem(assertionItem: IAssertion): void {
    if (assertionItem) {
      const assertionArr = [assertionItem];
      this.sendSocketEvent("16" + " " + JSON.stringify(assertionArr));
    }
  }

  PauseAndResumeRecord(action: string) {
    // true pause - false resume
    this.sendSocketEvent("48" + " " + JSON.stringify(action));
  }

  SendVariableNameItem(variableNameDetail): void {
    this.sendSocketEvent("40" + " " + JSON.stringify(variableNameDetail));
  }

  SendSelectedComponent(componentDetails): void {
    this.sendSocketEvent(`39  ${JSON.stringify(componentDetails)}`);
  }

  SendStartAutomatedRecording(
    isStartAsIs = false,
    isAutoHandleDialogs,
    autoHandleMode: "POSITIVE" | "NEGATIVE" | "DEFAULT" | "" = "",
    promptRecordingFeed = "",
    isSimplePromptMode = false
  ): void {
    if (promptRecordingFeed.length > 0) {
      let splitString = promptRecordingFeed.split("\n");
      let text = "";
      splitString.forEach((elem, index) => {
        if (index != splitString.length - 1 && elem) {
          text += `'${elem}'||`;
        } else if (elem) {
          text += `'${elem}'`;
        }
      });
      promptRecordingFeed = `{${text}}`;
    }

    if (isSimplePromptMode) {
      this.sendSocketEvent(
        `${ActionEvents.StartRecording.toString()} ${isStartAsIs} ${isAutoHandleDialogs} ${isSimplePromptMode} ${promptRecordingFeed}`
      );
    } else {
      const modeToken = !isAutoHandleDialogs && autoHandleMode ? ` ${autoHandleMode}` : "";
      this.sendSocketEvent(
        `${ActionEvents.StartRecording.toString()} ${isStartAsIs} ${isAutoHandleDialogs}${modeToken}${promptRecordingFeed}`
      );
    }
  }

  SendPerformActionEvent(event: IActionEvent): void {
    let eventStr = ActionEvents.PerformAction.toString();
    if (event.ActionEventEnum) {
      eventStr += " " + event.ActionEventEnum.toString();
    }
    if (event.Value) {
      eventStr += " " + event.Value;
    }
    this.sendSocketEvent(eventStr);
  }

  sendTimeDelayActionEvent(event: any) {
    let eventStr = ActionEvents.PerformAction.toString();
    if (event.actionEventEnum) {
      eventStr += " " + event.actionEventEnum.toString();
    }
    if (event.value) {
      eventStr += " " + event.value;
    }
    if (event.index >= 0) {
      eventStr += " " + event.index.toString();
    }
    if (event.isEdit) {
      eventStr += " " + event.isEdit;
    }
    this.sendSocketEvent(eventStr);
  }

  sendTestDataOverrideActionEvent(event: any) {
    let eventStr = ActionEvents.PerformAction.toString();
    if (event.actionEventEnum) {
      eventStr += " " + event.actionEventEnum.toString();
    }
    if (event.testDataID) {
      eventStr += " " + event.testDataID;
    }
    if (event.newValue) {
      eventStr += " " + event.newValue;
    }
    if (event.index >= 0) {
      eventStr += " " + event.index.toString();
    }
    if (event.isEdit) {
      eventStr += " " + event.isEdit;
    }
    if (event.isVariable !== undefined) {
      eventStr += " " + event.isVariable.toString();
    }
    console.log(eventStr);
    this.sendSocketEvent(eventStr);
  }

  sendCompareVariableActionEvent(event: any) {
    console.log(event);
    const eventStr = "46 " + JSON.stringify(event);
    console.log(eventStr);
    this.sendSocketEvent(eventStr);
  }

  OnSessionInit(): void {
    this.sessionService.updateSocketConnectionStatus(
      this.sessionService.SessionGUID
    );
    this.sendUserGuid();
    this.SendRefreshCleanUp();
    this.GetSessionThirdPartyApps();
    this.SendEndAutomatedRecordingDontSaveToCloud();
  }

  GetSessionThirdPartyApps(): void {
    this.sendSocketEvent("12");
  }

  GetCurrentMode(): void {
    this.sendSocketEvent("6");
  }

  sendUserGuid(): void {
    this.sendSocketEvent(
      `45 ${this.applicationService.parentGUID$.getValue()}`
    );
  }

  SendRefreshCleanUp(): void {
    this.sendSocketEvent("14");
  }

  SendGetResourceThreshold(): void {
    this.sendSocketEvent("17");
  }

  SendSetResourceThreshold(value: string): void {
    this.sendSocketEvent("18 " + value);
  }

  SendDeepLinkURL(data): void {
    this.sendSocketEvent(`1 111 ${data}`);
  }

  sendDeepLinkWithVariable(
    testDataVariableId?: string,
    testCaseVariableId?: string,
    apiStepVariableId?: string
  ): void {
    const variableId =
      testDataVariableId || testCaseVariableId || apiStepVariableId;
    const actionType = Actions.DEEP_LINK_WITH_VARIABLE;
    if (!variableId) {
      console.error("No Variables provided for Deep Link.");
      return;
    }
    console.log("[DeepLink Var][Socket] sending", {
      actionType,
      variableId,
    });
    this.sendSocketEvent(
      `${ActionEvents.PerformAction} ${actionType} ${variableId}`
    );
  }

  RequestPlaybackComponents(): void {
    this.sendSocketEvent("5 0");
  }

  SendStartDeviceLogs(): void {
    if (!this.SessionSocket) {
      return;
    }
    this.sendSocketEvent("8");
  }

  SendStopDeviceLogs(): void {
    if (!this.SessionSocket) {
      return;
    }
    this.sendSocketEvent("19");
  }
  
  SendStartHTTPSLogs(): void {
    if (!this.SessionSocket) {
      return;
    }
    this.sendSocketEvent("113");
  }

  SendStopHTTPSLogs(): void {
    if (!this.SessionSocket) {
      return;
    }
    this.sendSocketEvent("114");
  }

  SendStartNetworkLogs(): void {
    if (!this.SessionSocket) {
      return;
    }
    this.sendSocketEvent("111");
  }

  SendStopNetworkLogs(isDownload: boolean = false): void {
    if (!this.SessionSocket) {
      return;
    }
    const downloadFlag = isDownload ? "1" : "0";
    this.sendSocketEvent("112" + " " + downloadFlag);
  }

  SendMockLocation(latitude: string, longitude: string, isVariable: boolean): void {
    this.sendSocketEvent(`69 ${latitude} ${longitude} ${isVariable}`);
  }

  SendArithmeticExpression(event: any) {
    //console.log(event)
    // New format: 47 false 1 {"name":"Arithmetic object checking","expression":"{{25034}} + 7.1 * (2 - 1)","value":10.1,"variable_type":"Float","no_of_decimals":1}
    // Where: 47 is actionEventEnum, false is isInsertStep flag, 1 is index, followed by JSON data

    // Determine index and whether this is an actual insert or an append at the end
    let index = 0;
    let isInsertStep = "false";
    if (this.recordingService && this.recordingService.insertStepAtIndex) {
      const currentIndex = this.recordingService.insertStepAtIndex.getValue();
      const stepsLength =
        this.recordingService.automatedStepsRecorded.getValue().length;

      // If no specific index is set, append to the end
      if (currentIndex === null || currentIndex === undefined) {
        index = stepsLength > 0 ? stepsLength : 0;
        isInsertStep = "false";
      } else {
        // If index is set, decide if it's a true insert (not at the end) or append at last index
        index = currentIndex;
        isInsertStep = currentIndex < stepsLength ? "true" : "false";
      }
    }

    //false for new step and not replace(edit)
    const eventStr = `47 false ${index} ${JSON.stringify(event)}`;
    this.sendSocketEvent(eventStr);
  }

  ForceEndIDESession(): void {
    if (!this.SessionSocket) {
      return;
    }
    this.sendSocketEvent("4");
  }

  GetDynamicVariables(): void {
    this.sendSocketEvent("41");
  }

  sendNotificationValue(value: number): void {
    this.sendSocketEvent(`1 ${value}`);
  }

  GetAPISetVariable(): void {
    this.sendSocketEvent("44");
  }

  sendAppChange(appName: string = "") {
    this.sendSocketEvent(`1 420 ${appName}`);
  }

  sendPinchZoom(direction: 'in' | 'out', centerX: number, centerY: number, radius: number): void {
    const code = direction === 'in' ? 114 : 113;
    this.sendSocketEvent(`1 ${code} ${direction} ${centerX} ${centerY} ${radius}`);
  }

  sendLongPressDrag(flag): void {
    this.sendSocketEvent(`115 ${flag}`);
  }

  sendLongPress(x1: number, y1: number, x2: number, y2: number): void {
    this.sendSocketEvent(`1 117 ${x1} ${y1} ${x2} ${y2}`);
  }

  // Accessability Feature Sockets Events
  handleAccessibilityCompleted() {
    this.accessibilityTestCompleted.next(1);
    this.canvasDisabled.next(false);
  }

  handleAccessibilityStarted() {
    this.canvasDisabled.next(true);
  }

  handleDriverCrashedRestarting(ideMessage: string) {
    this.overLayService.showDriverCrashedOverlay();
    this.isDriverCrashedRestarting = true;
    const message = ideMessage.split(
      "           -------------------           "
    )[0];
    this.toasterService.ShowToastMessage(message, 2, 5000);
  }
  handleDriverOnline(ideMessage: string) {
    this.isDriverCrashedRestarting = false;
    const message = ideMessage.split(
      "           -------------------           "
    )[0];
    this.toasterService.ShowToastMessage(message, 1, 5000);
    this.overLayService.HideOverlay();
  }
  handleAccessibilityReportUploaded() {
    this.isAccessabilityReportUploaded.next(true);
  }
  handleAccessibilityReportGuid(ideMessage: any) {
    this.SocketIDEMessages.next(ideMessage);
  }
  handleNetworkLogsDownload(ideMessage: string) {
    const url = ideMessage.substring(ideMessage.indexOf(":") + 1).trim();
    this.SocketIDEMessages.next(url);
  }
  handleAccessibilityReportFailedUploaded() {
    this.isAccessabilityReportFailedUploaded.next(true);
  }
  sendStartAccessibilityMode() {
    this.sendSocketEvent("100");
  }
  sendStopAccessibilityMode() {
    this.sendSocketEvent("101");
  }
  saveAccessibilityReport(
    reportName: string,
    reportGuid: string,
    reportDescription: string = "test case",
    reportTags: any = []
  ) {
    const data = {
      report_name: reportName,
      report_guid: reportGuid,
      report_description: reportDescription,
      report_tags: reportTags,
    };
    this.sendSocketEvent(`102 ${JSON.stringify(data)}`);
  }
  pauseAccessibility() {
    this.sendSocketEvent("103");
  }
  resumeAccessibility() {
    this.sendSocketEvent("104");
  }
  sessionExpired() {
    this.sendSocketEvent("105");
  }
  // * -- SWIPE UNTIL FEATURE SOCKET EVENTS -- *
  sendStartSwipeUntil() {
    this.sendSocketEvent("106");
  }
  // sends socket event to let coreops know not to save the swipe-until actions performed
  sendCancelSwipeUntil() {
    this.sendSocketEvent("109");
  }
  sendAddSwipeUntil(swipeUntilData: string) {
    this.sendSocketEvent("109 " + swipeUntilData);
  }
  handleSwipeUntilComponentData(ideMessage: string) {
    this.SocketIDEMessages.next(ideMessage);
  }
  handleSwipeUntilUIChanges(ideMessage: string) {
    if (ideMessage.startsWith("Swipe Until Mode Started")) {
      this.recordingService.manageSwipeUntilUI(true);
    }
    if (
      ideMessage.startsWith("Saved Swipe Until Step") ||
      ideMessage.includes("Swipe Until Step Cancelled")
    ) {
      this.recordingService.manageSwipeUntilUI(false);
      this.utilsService.stepActionOverlay.next("");
    }
  }
  sendAddUiValidationStep(payload: any) {
    this.sendSocketEvent(`49 ${JSON.stringify(payload)}`);
  }
}
